import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from 'components/Page';
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DescriptionListItem from "components/DescriptionListItem";
import {Collection} from "types/Collection";
import Chip from "components/Chip";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import DeleteCollection from '../Delete';
import {CollectionIcon} from "@heroicons/react/outline";
import Empty from "components/Empty";
import {useOperator} from "context/OperatorContext";

// Helpers ----

function locationList(collection: Collection): string {
  return collection.exhibits
    .map(exhibit => exhibit.zone.name)
    .filter((name, index, self) => self.indexOf(name) === index)
    .join(', ')
}

// Component ----

function ViewCollection() {
  const {id} = useParams();
  const {data: collection, isLoading} = useQuery(
    ['collections', {id}],
    () => ngage.collections.get(Number(id))
  )

  const [deletingCollection, setDeletingCollection] = useState<Collection | null>(null);

  const {termFor} = useOperator();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  // Prepare action handlers
  function handleDeleteCollection() {
    if (collection) setDeletingCollection(collection)
  }

  function handleEditCollection() {
    if (id) navigate(`/collections/${id}/edit`)
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteCollection}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditCollection}>Edit</Button>
  </>

  return (
    <Page
      title={collection?.name || `View ${termFor('Collection')}`}
      breadcrumbs={{
        "Content": "/",
        [termFor('Collections')]: "/collections",
        [collection?.name || `View ${termFor('Collection')}`]: `/collections/${id}`
      }}
      actions={actions}
      isLoading={isLoading}
    >
      {collection ?
        <>
          {/* Collection Information */}
          <section>
            <Card
              heading={`${termFor('Collection')} Information`}
              subheading={`${collection.exhibit_count} exhibits`}
            >
              <DescriptionList>
                <DescriptionListItem span={1}>
                  <DescriptionTerm>Location</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{collection.exhibit_count ? locationList(collection) : 'N/A'}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Description</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">
                      {collection.about ?? (collection.exhibit_count ? `A collection of ${collection.exhibit_count} ${termFor('exhibits')}` : `An empty ${termFor('collection')}`)}
                    </p>
                  </DescriptionDetails>
                </DescriptionListItem>
              </DescriptionList>
            </Card>
          </section>

          {/* Exhibits */}
          <section className="mt-4">
            <Card heading={termFor('Exhibits')}>
              {collection.exhibits.length ?
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {collection.exhibits.map(exhibit => (
                    <li key={exhibit.id}>
                      <Chip
                        name={exhibit.name}
                        description={exhibit.type.name}
                        href={`/exhibits/${exhibit.id}`}
                        backgroundSrc={exhibit.image?.preview_url}
                      />
                    </li>
                  ))}
                </ul>
                :
                <Empty
                  icon={CollectionIcon}
                  text="No Exhibits"
                  hint={`Allocate ${termFor('exhibits')} to this ${termFor('collection')} to see them here.`}
                />
              }
            </Card>
          </section>
        </>
        : null}

      {/* Delete confirmation modal */}
      <DeleteCollection
        show={collection === deletingCollection}
        collection={deletingCollection}
        onCancel={() => setDeletingCollection(null)}
        onDelete={collection => {
          navigate('/collections')
          notify("success", `${termFor('Collection')} Deleted`, `${collection.name} has been permanently removed`)
        }}
        onDeleteFail={collection => {
          setDeletingCollection(null)
          notify("error", `${termFor('Collection')} Not Deleted`, `${collection.name} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewCollection;